import SortType from '../const/_sort-type'
import SeatClass from '../const/_seat-class'
import AreaCode from '../const/area_result'
import CountryCode from '../const/country_result'
import CityCode from '../const/city_result'

import Collection from '../service/_collection'
import { objectToGet, dateFormat } from './index'

import dayjs from 'dayjs'
import 'dayjs/locale/ja'
dayjs.locale(`ja`)

// ============================
// Air
// ============================

/**
 * getLocalStayTime // 現地滞在時間を返す
 *
 * @param {string} arrivalDate // 現地到着時間
 * @param {string} departureDate // 現地出発時間
 */
export function getLocalStayTime(arrivalDate, departureDate) {
  if (departureDate === null || arrivalDate === null) return ''
  const arrival = dayjs(arrivalDate)
  const departure = dayjs(departureDate)
  const hour = departure.diff(arrival, 'hour')
  const h = hour % 24
  const d = (hour / 24) | 0

  return `${d}日${h}時間`
}

/**
 * getTimeText // 到着日に「翌」や「前」を追加する
 *
 * @param {string} departureDate 2020-11-09 15:00など
 * @param {string} arrivalDate 2020-11-10 15:00など
 * @return {string} '', '翌'など
 */
export function getTmeText(departureDate, arrivalDate) {
  if (departureDate === null || arrivalDate === null) return ''
  const arrivalDay = dayjs(arrivalDate)
  const day1 = dayjs(departureDate).subtract(2, 'day')
  const day2 = dayjs(departureDate).subtract(1, 'day')
  const day3 = dayjs(departureDate).add(1, 'day')
  const day4 = dayjs(departureDate).add(2, 'day')

  if (arrivalDay.isSame(day1, 'day')) return '-2日 '
  if (arrivalDay.isSame(day2, 'day')) return '前 '
  if (arrivalDay.isSame(day3, 'day')) return '翌 '
  if (arrivalDay.isSame(day4, 'day')) return '+2日 '
  return ''
}

/**
 * carrierImage // 航空キャリアの画像URLを返す
 *
 * @param {string} carrierCode
 */
export function carrierImage(carrierCode) {
  if (carrierCode) return `/kaigai/assets/career/${carrierCode}.gif`
  return ''
}

/**
 * seatClassToText // 座席クラスをテキストに変換する
 *
 * @param {} data
 */
export function seatClassToText(key) {
  const item = SeatClass.find(item => item.string === key)
  return item.ja
}

export function diffFormatTime(time) {
  const data = time.split(':')
  return `${data[0]}時間${data[1]}分`
}

/**
 * diffFormatCarbonDate. // 日付差分を算出し、xx日間xx時間xx分のフォーマットに変換する
 *
 * @param {string} startDate yyyy-mm-dd H:i:s dayjsで処理可能な形式ならなんでも
 * @param {string} endDate
 * @return null|string データ不整合の場合はnullを返す
 */
export function diffFormatCarbonDate(startDate, endDate) {
  if (!startDate || !endDate) return null
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  // 開始時間より前の場合 null
  if (start.isAfter(end)) return null

  // 差分計算
  const diffDays = end.diff(start, 'day')
  let diffHours = end.diff(start, 'hour')
  let diffMinutes = end.diff(start, 'minute')

  // 日付差分を自然な日本語にする(0の日時分は非表示とする)
  let resultString = ''
  let stringKanFlg = false // 最後に"間"をつけるか否か
  if (diffDays > 0) {
    resultString += diffDays + '日'
  }
  if (diffHours > 0) {
    // 100hなら4日間分の96hを切り落とし4hとする
    diffHours = diffHours < 24 ? diffHours : diffHours % 24
    if (diffHours > 0) {
      stringKanFlg = true
      resultString += diffHours + '時間'
    }
  }
  // 100分なら1時間分の60を切り落とし40分とする
  diffMinutes = diffMinutes < 60 ? diffMinutes : diffMinutes % 60
  if (diffMinutes > 0 || (diffDays === 0 && diffHours === 0)) {
    // 0分のときは、0日0時間の場合のみ0分を表示する
    resultString += diffMinutes + '分'
  }
  // どこにも"間"が無いなら最後につける
  return stringKanFlg ? resultString : resultString + '間'
}

// ============================
// Tour
// ============================

/**
 * arrivalCode. // 目的地コードから各目的地コードを取得する
 *
 * @param {string} arrival_code // 目的地コードのいずれか
 */
export function arrivalCode(arrival_code) {
  const result = {}
  let countrySearch = arrival_code
  let areaSearch = arrival_code

  const city = new Collection(CityCode).find(item => item.code === arrival_code)
  if (city) result.cityCode = city.code
  if (city) result.cityCode3 = city.code3
  if (city) result.cityName = city.name
  if (city) countrySearch = city.country_code

  const country = new Collection(CountryCode).find(item => item.code === countrySearch)
  if (country) result.countryCode = country.code
  if (country) result.countryCode3 = country.code3
  if (country) result.countryName = country.name
  if (country) areaSearch = country.area_code

  const area = new Collection(AreaCode).find(item => item.code === areaSearch)
  if (area) result.areaCode = area.code
  if (area) result.areaName = area.name

  return result
}

/**
 * tourCodeToString // ツーアコードを文字列に変換する
 *
 * @param {object} code
 */
export function tourCodeToString(code) {
  // 目的地コード
  const arrivalCode = code.arrivalCode

  // 滞在日数
  const letter = code.letter

  // 便コード
  const flightNumber = code.flightNumber

  // シートクラス
  const seatClass = code.seatClass

  // EANコード
  const eanCode = code.eanCode

  return `${arrivalCode}${letter}${flightNumber}${seatClass}${eanCode}`
}

/**
 * tourCode // ツアーコードを変更する
 *
 * @param {object} code // ツアーコード
 * @param {string} name // 変更するものの名前
 * @param {string} data // 変更するデータ
 */
export function tourCode(code, name, data) {
  if (data === null) return {}

  // 目的地コード
  if (name === 'arrivalCode') code.arrivalCode = data

  // 滞在日数
  if (name === 'letter') code.letter = data

  // 便コード
  if (name === 'flightNumber') code.flightNumber = data

  // シートクラス
  if (name === 'seatClass') code.seatClass = data

  // EANコード
  if (name === 'eanCode') code.eanCode = data

  return code
}

// ============================
// SearchConditions
// ============================

/**
 * numberToSelectData // 数値からセレクトのデータを作成する 0 ~ 設定数値
 *
 * @param {number} minNum // 繰り返す値 から
 * @param {number} num // 繰り返す値 まで
 */
export function numberToSelectData(minNum = 0, num = 0) {
  const array = []
  for (let i = minNum; i <= num; i++) {
    array.push({ key: i, value: i })
  }
  return array
}

/**
 * areaCodeToSelectData // エリアコードの定義から選択肢のデータを作成する
 *
 * @param {string} code // エリアコード
 */
export function areaCodeToSelectData(code) {
  if (!code) return [{ key: 'none', value: '国・地域を選択' }]
  const dataArray = new Collection(CountryCode)
  const dataArray2 = new Collection(CountryCode)

  const array2 = dataArray2
    .filter(item => item.area_code === code)
    .filter(item => item.display === null)
    .sort(false, 'name_ja')
    .map(item => {
      return { key: item.code, value: item.name }
    })
    .get()

  const array = dataArray
    .filter(item => item.area_code === code)
    .filter(item => item.display !== null)
    .sort(false, 'display')
    .map(item => {
      return { key: item.code, value: item.name }
    })
    .unshift({ key: 'none', value: '国・地域を選択' })
    .concat(array2)
    .get()
  return array
}

/**
 * countryCodeToSelectData // 国コードの定義から選択肢のデータを作成する
 *
 * @param {string} code // 国コード
 */
export function countryCodeToSelectData(code) {
  if (!code) return [{ key: 'none', value: '都市を選択' }]
  const dataArray = new Collection(CityCode)

  const array = dataArray
    .filter(item => item.country_code === code)
    .sort(false, 'display')
    .map(item => {
      return { key: item.code, value: item.name_ja }
    })
    .unshift({ key: 'none', value: '都市を選択' })
    .get()
  return array
}

// ============================
// テンプレート
// ============================

/**
 * tagListTemplate // タグのhtmlを作成
 *
 * @param {object} data
 */
export function tagListTemplate(dataList) {
  let tags = ''

  if (Object.keys(dataList).length === 0) return ''
  Object.values(dataList).forEach(item => {
    tags += `<div class="tagList2__item ${item.class}">${item.text}</div>`
  })
  if (tags === '') return ''
  return `<div class="tourAboutBox__tapWap">
              <div class="tagList2">
                  ${tags}
              </div>
          </div>`
}

export function convertPayLocallyType(type) {
  if (type === 'ResortFee') return 'ResortFee'
  if (type === 'MandatoryTax') return 'MandatoryTax'
  if (type === 'MandatoryFee') return 'MandatoryFee'
  return type
}

export function convertPayLocallyUnit(type) {
  if (type === 'Per person') return '１人あたり'
  if (type === 'Per room') return '１部屋あたり'
  if (type === 'Per accommodation') return '１宿泊施設あたり'
  if (type === 'Per house') return '１家あたり'
  if (type === 'Per apartment') return '１アパートあたり'
  if (type === 'Per adult') return '大人１人あたり'
  return type
}

export function convertPayLocallyFrequency(type) {
  if (type === 'Per night') return '１泊あたり'
  if (type === 'Per day') return '１日あたり'
  if (type === 'Per stay') return '１滞在あたり'
  if (type === 'Per week') return '一週間'
  if (type === 'Round trip') return '往復'
  if (type === 'One way') return '片道'
  return type
}

// ============================
// その他
// ============================

export function dataArrivalCode(data) {
  if (data.cityCode && data.cityCode !== 'none') return data.cityCode
  if (data.countryCode && data.countryCode !== 'none') return data.countryCode
  if (data.areaCode) return data.areaCode
  return null
}

export function dataFareType(boolean) {
  // 30日40日...のチェックありはITそれ以外はPEXで検索
  let fare_type = 'PEX'
  if (boolean) fare_type = 'IT'
  return fare_type
}

export function dataSortType(sortType) {
  const type = SortType.find(item => item.string === sortType)
  return type.value
}

export function dataAreaCodeList() {
  const dataArray = new Collection(AreaCode)
  const array = dataArray
    .map(item => {
      return { key: item.code, value: item.name }
    })
    // .unshift({ key: '', value: '' })
    .get()
  return array
}

export function tagList(array) {
  const array2 = []
  let pexFlg = false
  array.map(item => {
    if (objectToGet(item, 'word', null) === '正規割引運賃（PEX運賃）利用') {
      item.word = '即日取消料発生'
      pexFlg = true
      array2.push(item)
    } else {
      if (item.managed === true) {
        array2.push(item)
      }
    }
  })
  if (pexFlg === false) {
    // PEX文言なければITタグ追加
    const it = { word: '出発日前日の30・40日前まで取消料無料', managed: false }
    array2.unshift(it)
  }
  return array2.map(item => {
    let cssClass = ''
    if (objectToGet(item, 'managed', false)) cssClass = 'tagList__item--orange'
    var chargeType = objectToGet(item, 'word', null)
    if (chargeType === '即日取消料発生' || chargeType === '出発日前日の30・40日前まで取消料無料')
      cssClass = 'tagList__item--red'
    return {
      text: objectToGet(item, 'word', ''),
      class: cssClass,
    }
  })
}

export function flightList(array) {
  return array.map(item => {
    const departureDate = objectToGet(item, 'departure_datetime')
    const arrivalDate = objectToGet(item, 'arrival_datetime')
    const timeText = getTmeText(departureDate, arrivalDate)
    const t = dateFormat(arrivalDate, 'HH:mm')
    const time = `${timeText}${t}`

    return {
      no: objectToGet(item, 'no'),
      departureAirportCode: objectToGet(item, 'departure_airport_code'),
      departureAirportName: objectToGet(item, 'departure_airport_name'),
      departureDate: dateFormat(objectToGet(item, 'departure_datetime'), 'HH:mm'),
      arrivalAirportCode: objectToGet(item, 'arrival_airport_code'),
      arrivalAirportName: objectToGet(item, 'arrival_airport_name'),
      arrivalDate: objectToGet(item, 'arrival_datetime'),
      flightTime: objectToGet(item, 'flight_time'),
      time: time,
      carrierCode: objectToGet(item, 'carrier_code'),
      carrierName: objectToGet(item, 'carrier_name'),
      flightNo: objectToGet(item, 'flight_no'),
      seatClass: seatClassToText(objectToGet(item, 'seat_class', '')),
    }
  })
}

export function hotel(data) {
  return {
    no: objectToGet(data, 'arrange_no'),
    enableArrange: objectToGet(data, 'enable_arrange', false), // ホテルアレンジ可否
    cityCode: objectToGet(data, 'city_code'),
    cityName: objectToGet(data, 'city_name'),
    hotelCode: objectToGet(data, 'hotel_code'),
    hotelName: objectToGet(data, 'hotel_name'),
    roomType: objectToGet(data, 'room_type'),
    roomName: objectToGet(data, 'room_name'),
    hotelGrade: objectToGet(data, 'hotel_rank'),
    checkInCate: objectToGet(data, 'check_in_date'),
    stayDays: objectToGet(data, 'stay_days'),
    images: objectToGet(data, 'images', []),
    mealCode: objectToGet(data, 'meal_code', ''),
  }
}

export function mainImage(array) {
  return array.find(value => value.top_image_flag === true)
}

/**
 * mealCountFunc.
 *
 * @param {} array
 * @param {} label
 */
export function mealCountFunc(array, label) {
  return new Collection(array)
    .pickup(label, true)
    .filter(item => !(item === 'なし' || item === '機内食'))
    .count()
}

export function convertCountryCodeList(cityCode) {
  const array = CityCode.filter(data => data.code === cityCode)
  return array.map(data => data.country_code)
}

export function getTourUrl(tourKey, departureDate, airNo, hotelNo, transferNo) {
  const origin = location.origin

  if (transferNo === 0) {
    return `${origin}/kaigai/plan?tour=${tourKey}&departure-date=${departureDate}&hotel=${hotelNo}&flight=${airNo}`
  }
  return `${origin}/kaigai/plan?tour=${tourKey}&departure-date=${departureDate}&hotel=${hotelNo}&flight=${airNo}&transfer=${transferNo}`
}
